import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'

import { Alert } from '@/components/ui/alert/alert'
import { Button } from '@/components/ui/button/button'
import { Form } from '@/components/ui/form/form'
import { Heading } from '@/components/ui/heading/heading'
import { Input } from '@/components/ui/input/input'
import { Text } from '@/components/ui/text/text'
import { toast } from '@/components/ui/toaster/util'

import { useAuth } from '@/libs/hooks/use-auth'

import { ApiError } from '@/features/api'
import { authLogin } from '@/features/auth/api'
import { AuthLoginDto } from '@/features/auth/type'

export const Route = createFileRoute('/auth/login')({
	head: () => ({
		meta: [{ title: 'IKKI Pos | Login' }],
	}),
	component: Component,
})

const toastId = 'login'

function Component() {
	const navigate = Route.useNavigate()
	const doLogin = useAuth((s) => s.login)
	const form = useForm<AuthLoginDto>({
		resolver: zodResolver(AuthLoginDto),
		defaultValues: getDefaultValues(),
	})

	const { control, setError, formState } = form

	const { mutate, isPending } = useMutation({
		mutationFn: async (data: AuthLoginDto) => {
			const { token, name } = await authLogin.api({ body: data })
			await doLogin(token)
			return { name }
		},
		onSuccess: ({ name }) => {
			toast.success(`Selamat datang ${name}`, { id: toastId })
			navigate({ to: '/', replace: true })
		},
		onError: (err) => {
			if (ApiError.is(err)) {
				if (
					err.code === 'EMAIL_NOT_FOUND' ||
					err.code === 'INCORRECT_PASSWORD'
				) {
					setError('root', {
						type: 'validate',
						message: 'Email atau password salah',
					})
				}
			}
		},
	})

	const handleOnSubmit = form.handleSubmit(async (data) => mutate(data))
	const rootError = formState.errors.root?.message

	return (
		<Form {...form}>
			<form
				onSubmit={handleOnSubmit}
				className="inline-flex w-full max-w-[320px] flex-col"
			>
				<div className="mb-4 flex w-full flex-col items-center text-center">
					<Heading>IKKI Smart Management</Heading>
					<Text size="small" className="text-center text-ui-fg-subtle">
						Masuk untuk mengakses
					</Text>
				</div>

				<div className="mt-4 flex w-full flex-col gap-y-3">
					<Form.Field
						control={control}
						name="email"
						render={({ field }) => {
							return (
								<Form.Item>
									<Form.Control>
										<Input
											type="email"
											autoComplete="email"
											{...field}
											className="bg-ui-bg-field"
											placeholder="Email"
										/>
									</Form.Control>
								</Form.Item>
							)
						}}
					/>
					<Form.Field
						control={control}
						name="password"
						render={({ field }) => {
							return (
								<Form.Item>
									<Form.Control>
										<Input
											type="password"
											autoComplete="current-password"
											{...field}
											className="bg-ui-bg-field"
											placeholder="Password"
										/>
									</Form.Control>
								</Form.Item>
							)
						}}
					/>
					{rootError && <Alert variant="error">{rootError}</Alert>}
					<Button className="mt-2 w-full" type="submit" isLoading={isPending}>
						Masuk
					</Button>
				</div>
			</form>
		</Form>
	)
}

function getDefaultValues(): AuthLoginDto {
	return {
		email: '',
		password: '',
	}
}
